import { verifiedSymbol, getEventHash, verifyEvent } from "nostr-tools/pure";
import { cached, mapVals, first, pick, now } from "@welshman/lib";
import { getReplyTagValues, getCommentTagValues } from "./Tags.js";
import { getAddress, Address } from "./Address.js";
import { COMMENT, isEphemeralKind, isReplaceableKind, isPlainReplaceableKind, isParameterizedReplaceableKind, } from "./Kinds.js";
export const createEvent = (kind, { content = "", tags = [], created_at = now() } = {}) => ({ kind, content, tags, created_at });
export const isEventTemplate = (e) => Boolean(typeof e.kind === "number" && Array.isArray(e.tags) && typeof e.content === "string");
export const isStampedEvent = (e) => Boolean(isEventTemplate(e) && e.created_at >= 0);
export const isOwnedEvent = (e) => Boolean(isStampedEvent(e) && e.pubkey);
export const isHashedEvent = (e) => Boolean(isOwnedEvent(e) && e.id);
export const isSignedEvent = (e) => Boolean(isHashedEvent(e) && e.sig);
export const isUnwrappedEvent = (e) => Boolean(isHashedEvent(e) && e.wrap);
export const isTrustedEvent = (e) => isSignedEvent(e) || isUnwrappedEvent(e);
export const asEventTemplate = (e) => pick(["kind", "tags", "content"], e);
export const asStampedEvent = (e) => pick(["kind", "tags", "content", "created_at"], e);
export const asOwnedEvent = (e) => pick(["kind", "tags", "content", "created_at", "pubkey"], e);
export const asHashedEvent = (e) => pick(["kind", "tags", "content", "created_at", "pubkey", "id"], e);
export const asSignedEvent = (e) => pick(["kind", "tags", "content", "created_at", "pubkey", "id", "sig"], e);
export const asUnwrappedEvent = (e) => pick(["kind", "tags", "content", "created_at", "pubkey", "id", "wrap"], e);
export const asTrustedEvent = (e) => pick(["kind", "tags", "content", "created_at", "pubkey", "id", "sig", "wrap"], e);
const _hasValidSignature = cached({
    maxSize: 10000,
    getKey: ([e]) => {
        try {
            return `${getEventHash(e)}:${e.sig}`;
        }
        catch (err) {
            return "invalid";
        }
    },
    getValue: ([e]) => {
        try {
            verifyEvent(e);
        }
        catch (err) {
            return false;
        }
        return true;
    },
});
export const hasValidSignature = (e) => e[verifiedSymbol] || _hasValidSignature(e);
export const getIdentifier = (e) => e.tags.find(t => t[0] === "d")?.[1];
export const getIdOrAddress = (e) => (isReplaceable(e) ? getAddress(e) : e.id);
export const getIdAndAddress = (e) => isReplaceable(e) ? [e.id, getAddress(e)] : [e.id];
export const isEphemeral = (e) => isEphemeralKind(e.kind);
export const isReplaceable = (e) => isReplaceableKind(e.kind);
export const isPlainReplaceable = (e) => isPlainReplaceableKind(e.kind);
export const isParameterizedReplaceable = (e) => isParameterizedReplaceableKind(e.kind);
export const getAncestors = ({ kind, tags }) => kind === COMMENT ? getCommentTagValues(tags) : getReplyTagValues(tags);
export const getParentIdsAndAddrs = (event) => {
    const { roots, replies } = getAncestors(event);
    return replies.length > 0 ? replies : roots;
};
export const getParentIdOrAddr = (event) => first(getParentIdsAndAddrs(event));
export const getParentIds = (event) => {
    const { roots, replies } = mapVals(ids => ids.filter(id => !Address.isAddress(id)), getAncestors(event));
    return replies.length > 0 ? replies : roots;
};
export const getParentId = (event) => first(getParentIds(event));
export const getParentAddrs = (event) => {
    const { roots, replies } = mapVals(ids => ids.filter(id => Address.isAddress(id)), getAncestors(event));
    return replies.length > 0 ? replies : roots;
};
export const getParentAddr = (event) => first(getParentAddrs(event));
export const isChildOf = (child, parent) => {
    const idsAndAddrs = getParentIdsAndAddrs(child);
    return getIdAndAddress(parent).some(x => idsAndAddrs.includes(x));
};
//# sourceMappingURL=Events.js.map